import {
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getClientAPI } from "../service/UserService";
import { getAllMatchListAPI, marketAnalysisAPI, getDiamondCasinoSummaryAPI } from "../service/AuthService";
import moment from "moment";
import "./Home.css";

const formatNumber = (num) => {
  if (num === undefined || num === null) return "0";
  return Number(num).toLocaleString("en-IN", { maximumFractionDigits: 2 });
};


function getDateRange(filterKey) {
  const todayEnd = moment().endOf("day");
  switch (filterKey) {
    case "today":
      return {
        from: moment().startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        to: todayEnd.format("YYYY-MM-DDTHH:mm:ss"),
      };
    case "week":
      return {
        from: moment().startOf("isoWeek").format("YYYY-MM-DDTHH:mm:ss"),
        to: todayEnd.format("YYYY-MM-DDTHH:mm:ss"),
      };
    case "month":
      return {
        from: moment().startOf("month").format("YYYY-MM-DDTHH:mm:ss"),
        to: todayEnd.format("YYYY-MM-DDTHH:mm:ss"),
      };
    default:
      return {};
  }
}

const countMatches = (sportData) => {
  if (!sportData || typeof sportData !== "object") return 0;
  return Object.values(sportData).reduce(
    (total, matches) => total + (Array.isArray(matches) ? matches.length : 0),
    0
  );
};

export default function Home() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalTitle, setModalTitle] = useState("");
  const [modalClients, setModalClients] = useState([]);
  const [modalSearch, setModalSearch] = useState("");
  const [modalPageSize, setModalPageSize] = useState(25);
  const [modalPage, setModalPage] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [sportsMatches, setSportsMatches] = useState({ cricket: {}, soccer: {}, tennis: {} });
  const [marketData, setMarketData] = useState([]);
  const [casinoStats, setCasinoStats] = useState({ totalStake: 0, totalPayout: 0, netPL: 0 });
  const [sportsLoading, setSportsLoading] = useState(true);
  const [marketFilter, setMarketFilter] = useState("all");
  const [marketFetching, setMarketFetching] = useState(false);
  const [customFrom, setCustomFrom] = useState(moment().subtract(7, "days").format("YYYY-MM-DD"));
  const [customTo, setCustomTo] = useState(moment().format("YYYY-MM-DD"));

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const { response } = await getClientAPI("", 0, 500);
        if (response?.childrenUserResponseDTOSPage?.content) {
          setClients(response.childrenUserResponseDTOSPage.content);
        }
      } catch {
        /* silently fail */
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, []);

  const fetchCasinoStats = useCallback(async (dateParams = {}) => {
    try {
      const payload = {};
      if (dateParams.from) payload.from = new Date(dateParams.from).toISOString();
      if (dateParams.to) payload.to = new Date(dateParams.to).toISOString();
      const casinoRes = await getDiamondCasinoSummaryAPI(payload);
      const summaryData = casinoRes?.response || casinoRes?.data || {};
      const totals = summaryData.totals || summaryData;
      setCasinoStats({
        totalStake: totals.totalStake || 0,
        totalPayout: totals.totalPayout || 0,
        netPL: totals.netPL != null ? totals.netPL : (totals.totalStake || 0) - (totals.totalPayout || 0),
      });
    } catch {
      /* casino stats optional */
    }
  }, []);

  useEffect(() => {
    const fetchSportsData = async () => {
      try {
        const [matchRes, marketRes] = await Promise.all([
          getAllMatchListAPI(),
          marketAnalysisAPI(),
        ]);
        if (matchRes?.response) {
          setSportsMatches({
            cricket: matchRes.response.cricket || {},
            soccer: matchRes.response.soccer || {},
            tennis: matchRes.response.tennis || {},
          });
        }
        if (marketRes?.response) {
          setMarketData(marketRes.response);
        }
      } catch {
        /* silently fail */
      }
      // "All" filter: no date params — returns all-time data
      await fetchCasinoStats();
      setSportsLoading(false);
    };
    fetchSportsData();
  }, [fetchCasinoStats]);

  const refetchMarketData = useCallback(async (params = {}) => {
    setMarketFetching(true);
    try {
      const [marketRes] = await Promise.all([
        marketAnalysisAPI(params),
        fetchCasinoStats(params),
      ]);
      if (marketRes?.response) {
        setMarketData(marketRes.response);
      }
    } catch {
      /* silently fail */
    } finally {
      setMarketFetching(false);
    }
  }, [fetchCasinoStats]);

  const handleMarketFilterChange = useCallback(
    (e) => {
      const filterKey = e.target.value;
      setMarketFilter(filterKey);
      if (filterKey === "custom") return;
      refetchMarketData(getDateRange(filterKey));
    },
    [refetchMarketData]
  );

  const handleCustomApply = useCallback(() => {
    if (!customFrom || !customTo) return;
    refetchMarketData({
      from: moment(customFrom).startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      to: moment(customTo).endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    });
  }, [customFrom, customTo, refetchMarketData]);

  const grouped = useMemo(() => {
    const groups = {};
    clients.forEach((client) => {
      const type = client.userType || "OTHER";
      if (!groups[type]) {
        groups[type] = {
          count: 0,
          totalBalance: 0,
          totalCredit: 0,
          totalExposure: 0,
          totalPl: 0,
          active: 0,
          inactive: 0,
          betLocked: 0,
          clients: [],
        };
      }
      const g = groups[type];
      g.count++;
      g.totalBalance += client.balance || 0;
      g.totalCredit += client.creditReference || 0;
      g.totalExposure += client.liability || 0;
      g.totalPl += client.ownPl || 0;
      if (client.isActive) g.active++;
      else g.inactive++;
      if (client.isBetLocked) g.betLocked++;
      g.clients.push(client);
    });
    return groups;
  }, [clients]);

  const sportStats = useMemo(() => {
    const sports = { Cricket: { bets: 0, stake: 0, profit: 0, loss: 0 }, Soccer: { bets: 0, stake: 0, profit: 0, loss: 0 }, Tennis: { bets: 0, stake: 0, profit: 0, loss: 0 } };
    (marketData || []).forEach((game) => {
      const sport = game.sport || "";
      const key = sport.charAt(0).toUpperCase() + sport.slice(1).toLowerCase();
      if (sports[key]) {
        sports[key].bets += game.totalBets || 0;
        sports[key].stake += game.totalStake || game.totalAmount || 0;
        (game.runnerPlExposureDTOS || []).forEach((r) => {
          const exp = r.exposure || 0;
          if (exp > 0) sports[key].profit += exp;
          else sports[key].loss += exp;
        });
      }
    });
    return {
      cricket: { ...sports.Cricket, matches: countMatches(sportsMatches.cricket) },
      soccer: { ...sports.Soccer, matches: countMatches(sportsMatches.soccer) },
      tennis: { ...sports.Tennis, matches: countMatches(sportsMatches.tennis) },
      casino: casinoStats,
    };
  }, [marketData, sportsMatches, casinoStats]);

  const totalCount = clients.length;
  const masterCount = (grouped.MASTER?.count || 0) + (grouped.SUPER?.count || 0) + (grouped.SUPER_ADMIN?.count || 0) + (grouped.ADMIN?.count || 0);
  const userCount = grouped.CLIENT?.count || 0;
  const agentCount = grouped.AGENT?.count || 0;
  const loginUsers = clients.filter((c) => c.isActive).length;
  const notLoginUsers = clients.filter((c) => !c.isActive).length;
  const blockedUsers = clients.filter((c) => c.isBetLocked).length;
  const totalBalance = clients.reduce((s, c) => s + (c.balance || 0), 0);
  const totalPl = clients.reduce((s, c) => s + (c.ownPl || 0), 0);
  const totalExposure = clients.reduce((s, c) => s + (c.liability || 0), 0);
  const totalCredit = clients.reduce((s, c) => s + (c.creditReference || 0), 0);

  const openModal = (title, clientList) => {
    setModalTitle(title);
    setModalClients(clientList);
    setModalSearch("");
    setModalPage(0);
    onOpen();
  };

  const filteredModalClients = useMemo(() => {
    if (!modalSearch) return modalClients;
    const q = modalSearch.toLowerCase();
    return modalClients.filter(
      (c) =>
        c.username?.toLowerCase().includes(q) ||
        c.name?.toLowerCase().includes(q) ||
        String(c.balance).includes(q)
    );
  }, [modalClients, modalSearch]);

  const totalModalPages = Math.ceil(filteredModalClients.length / modalPageSize);
  const startIdx = modalPage * modalPageSize;
  const displayedModalClients = filteredModalClients.slice(startIdx, startIdx + modalPageSize);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="100vh">
        <Spinner size="xl" thickness="4px" speed="0.65s" color="green.500" />
      </Box>
    );
  }

  const typeOrder = ["SUPER_ADMIN", "ADMIN", "SUPER", "MASTER", "AGENT", "CLIENT"];
  const typeLabels = {
    SUPER_ADMIN: "Super Admin",
    ADMIN: "Admin",
    SUPER: "Super",
    MASTER: "Master",
    AGENT: "Agent",
    CLIENT: "Client",
  };

  return (
    <Box w="100%" className="home-dashboard" p={{ base: "12px", md: "20px" }}>
      <div className="dashboard-grid">
        {/* Sport Stats Date Filter */}
        <div className="stats-card full-width">
          <div className="stats-card-header">
            <h3><i className="fas fa-filter" /> Sports P&L Filter</h3>
          </div>
          <div className="stats-card-body">
            <div className="date-filter-bar date-filter-bar--home">
              <select
                className="date-filter-select"
                value={marketFilter}
                onChange={handleMarketFilterChange}
                aria-label="Filter sports stats by date range"
              >
                <option value="all">All</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="custom">Custom Range</option>
              </select>

              {marketFilter === "custom" && (
                <div className="date-filter-custom">
                  <input
                    type="date"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    aria-label="From date"
                    className="date-filter-input"
                  />
                  <span className="date-filter-separator">to</span>
                  <input
                    type="date"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    aria-label="To date"
                    className="date-filter-input"
                  />
                  <button
                    type="button"
                    className="date-filter-apply"
                    onClick={handleCustomApply}
                    disabled={!customFrom || !customTo || marketFetching}
                  >
                    {marketFetching ? "Loading..." : "Apply"}
                  </button>
                </div>
              )}

              {marketFetching && (
                <Spinner size="sm" thickness="3px" speed="0.65s" color="yellow.500" />
              )}
            </div>
          </div>
        </div>

        {/* Sport Stats Section */}
        <div className={`stats-card sport-card${marketFetching ? " sport-card--fetching" : ""}`}>
          <div className="stats-card-header sport-header sport-header--cricket">
            <h3><i className="fas fa-cricket" /> Cricket</h3>
          </div>
          <div className="stats-card-body">
            <div className="stats-row">
              <span className="stats-row-label">Active Matches</span>
              <span className="stats-row-colon">:</span>
              <span className="stats-row-value">{sportsLoading ? "..." : sportStats.cricket.matches}</span>
            </div>
            <div className="stats-row">
              <span className="stats-row-label">Total Stake</span>
              <span className="stats-row-colon">:</span>
              <span className="stats-row-value">{sportsLoading ? "..." : <>{formatNumber(sportStats.cricket.stake)} ({sportStats.cricket.bets} bets)</>}</span>
            </div>
            <div className="stats-row">
              <span className="stats-row-label">Profit</span>
              <span className="stats-row-colon">:</span>
              <span className="stats-row-value green">{sportsLoading ? "..." : formatNumber(sportStats.cricket.profit)}</span>
            </div>
            <div className="stats-row">
              <span className="stats-row-label">Loss</span>
              <span className="stats-row-colon">:</span>
              <span className="stats-row-value red">{sportsLoading ? "..." : formatNumber(sportStats.cricket.loss)}</span>
            </div>
            <div className="stats-row">
              <span className="stats-row-label">Net P&L</span>
              <span className="stats-row-colon">:</span>
              <span className={`stats-row-value ${(sportStats.cricket.profit + sportStats.cricket.loss) >= 0 ? "green" : "red"}`}>
                {sportsLoading ? "..." : formatNumber(sportStats.cricket.profit + sportStats.cricket.loss)}
              </span>
            </div>
          </div>
          <div className="stats-card-divider sport-divider--cricket" />
        </div>

        <div className={`stats-card sport-card${marketFetching ? " sport-card--fetching" : ""}`}>
          <div className="stats-card-header sport-header sport-header--football">
            <h3><i className="fas fa-futbol" /> Football</h3>
          </div>
          <div className="stats-card-body">
            <div className="stats-row">
              <span className="stats-row-label">Active Matches</span>
              <span className="stats-row-colon">:</span>
              <span className="stats-row-value">{sportsLoading ? "..." : sportStats.soccer.matches}</span>
            </div>
            <div className="stats-row">
              <span className="stats-row-label">Total Stake</span>
              <span className="stats-row-colon">:</span>
              <span className="stats-row-value">{sportsLoading ? "..." : <>{formatNumber(sportStats.soccer.stake)} ({sportStats.soccer.bets} bets)</>}</span>
            </div>
            <div className="stats-row">
              <span className="stats-row-label">Profit</span>
              <span className="stats-row-colon">:</span>
              <span className="stats-row-value green">{sportsLoading ? "..." : formatNumber(sportStats.soccer.profit)}</span>
            </div>
            <div className="stats-row">
              <span className="stats-row-label">Loss</span>
              <span className="stats-row-colon">:</span>
              <span className="stats-row-value red">{sportsLoading ? "..." : formatNumber(sportStats.soccer.loss)}</span>
            </div>
            <div className="stats-row">
              <span className="stats-row-label">Net P&L</span>
              <span className="stats-row-colon">:</span>
              <span className={`stats-row-value ${(sportStats.soccer.profit + sportStats.soccer.loss) >= 0 ? "green" : "red"}`}>
                {sportsLoading ? "..." : formatNumber(sportStats.soccer.profit + sportStats.soccer.loss)}
              </span>
            </div>
          </div>
          <div className="stats-card-divider sport-divider--football" />
        </div>

        <div className={`stats-card sport-card${marketFetching ? " sport-card--fetching" : ""}`}>
          <div className="stats-card-header sport-header sport-header--tennis">
            <h3><i className="fas fa-table-tennis" /> Tennis</h3>
          </div>
          <div className="stats-card-body">
            <div className="stats-row">
              <span className="stats-row-label">Active Matches</span>
              <span className="stats-row-colon">:</span>
              <span className="stats-row-value">{sportsLoading ? "..." : sportStats.tennis.matches}</span>
            </div>
            <div className="stats-row">
              <span className="stats-row-label">Total Stake</span>
              <span className="stats-row-colon">:</span>
              <span className="stats-row-value">{sportsLoading ? "..." : <>{formatNumber(sportStats.tennis.stake)} ({sportStats.tennis.bets} bets)</>}</span>
            </div>
            <div className="stats-row">
              <span className="stats-row-label">Profit</span>
              <span className="stats-row-colon">:</span>
              <span className="stats-row-value green">{sportsLoading ? "..." : formatNumber(sportStats.tennis.profit)}</span>
            </div>
            <div className="stats-row">
              <span className="stats-row-label">Loss</span>
              <span className="stats-row-colon">:</span>
              <span className="stats-row-value red">{sportsLoading ? "..." : formatNumber(sportStats.tennis.loss)}</span>
            </div>
            <div className="stats-row">
              <span className="stats-row-label">Net P&L</span>
              <span className="stats-row-colon">:</span>
              <span className={`stats-row-value ${(sportStats.tennis.profit + sportStats.tennis.loss) >= 0 ? "green" : "red"}`}>
                {sportsLoading ? "..." : formatNumber(sportStats.tennis.profit + sportStats.tennis.loss)}
              </span>
            </div>
          </div>
          <div className="stats-card-divider sport-divider--tennis" />
        </div>

        <div className={`stats-card sport-card${marketFetching ? " sport-card--fetching" : ""}`}>
          <div className="stats-card-header sport-header sport-header--casino">
            <h3><i className="fas fa-dice" /> Casino</h3>
          </div>
          <div className="stats-card-body">
            <div className="stats-row">
              <span className="stats-row-label">Total Stake</span>
              <span className="stats-row-colon">:</span>
              <span className="stats-row-value">{sportsLoading ? "..." : formatNumber(casinoStats.totalStake)}</span>
            </div>
            <div className="stats-row">
              <span className="stats-row-label">Total Payout</span>
              <span className="stats-row-colon">:</span>
              <span className="stats-row-value">{sportsLoading ? "..." : formatNumber(casinoStats.totalPayout)}</span>
            </div>
            <div className="stats-row">
              <span className="stats-row-label">Net P&L</span>
              <span className="stats-row-colon">:</span>
              <span className={`stats-row-value ${casinoStats.netPL >= 0 ? "green" : "red"}`}>
                {sportsLoading ? "..." : formatNumber(casinoStats.netPL)}
              </span>
            </div>
          </div>
          <div className="stats-card-divider sport-divider--casino" />
        </div>

        {/* All Users Card */}
        <div className="stats-card" onClick={() => openModal("All Users", clients)}>
          <div className="stats-card-header">
            <h3>All Users</h3>
          </div>
          <div className="stats-card-body">
            <div className="stats-row">
              <span className="stats-row-label">
                <i className="fas fa-users-cog row-icon" />
                All Master
              </span>
              <span className="stats-row-colon">:</span>
              <span className="stats-row-value">{masterCount}</span>
            </div>
            <div className="stats-row">
              <span className="stats-row-label">
                <i className="fas fa-users row-icon" />
                All Users
              </span>
              <span className="stats-row-colon">:</span>
              <span className="stats-row-value">{totalCount}</span>
            </div>
            <div className="stats-row">
              <span className="stats-row-label">Login Users</span>
              <span className="stats-row-colon">:</span>
              <span className="stats-row-value green">{loginUsers}</span>
            </div>
            <div className="stats-row">
              <span className="stats-row-label">Not Login Users</span>
              <span className="stats-row-colon">:</span>
              <span className="stats-row-value red">{notLoginUsers}</span>
            </div>
            <div className="stats-row">
              <span className="stats-row-label">Blocked Users</span>
              <span className="stats-row-colon">:</span>
              <span className="stats-row-value green">{blockedUsers}</span>
            </div>
          </div>
          <div className="stats-card-divider" />
        </div>

        {/* Account Summary Card */}
        <div className="stats-card">
          <div className="stats-card-header">
            <h3>Account Summary</h3>
          </div>
          <div className="stats-card-body">
            <div className="stats-row">
              <span className="stats-row-label">Total Balance</span>
              <span className="stats-row-colon">:</span>
              <span className="stats-row-value">{formatNumber(totalBalance)}</span>
            </div>
            <div className="stats-row">
              <span className="stats-row-label">Total Credit</span>
              <span className="stats-row-colon">:</span>
              <span className="stats-row-value">{formatNumber(totalCredit)}</span>
            </div>
            <div className="stats-row">
              <span className="stats-row-label">Total Exposure</span>
              <span className="stats-row-colon">:</span>
              <span className={`stats-row-value ${totalExposure < 0 ? "red" : ""}`}>
                {formatNumber(totalExposure)}
              </span>
            </div>
            <div className="stats-row">
              <span className="stats-row-label">Profit/Loss</span>
              <span className="stats-row-colon">:</span>
              <span className={`stats-row-value ${totalPl >= 0 ? "green" : "red"}`}>
                {formatNumber(totalPl)}
              </span>
            </div>
          </div>
          <div className="stats-card-divider" />
        </div>

        {/* Per-type category cards */}
        {typeOrder
          .filter((t) => grouped[t])
          .map((type) => {
            const data = grouped[type];
            const label = typeLabels[type] || type;
            return (
              <div
                className="stats-card"
                key={type}
                onClick={() => openModal(label + " Users", data.clients)}
              >
                <div className="stats-card-header">
                  <h3>{label} Accounts</h3>
                </div>
                <div className="stats-card-body">
                  <div className="stats-row">
                    <span className="stats-row-label">Total</span>
                    <span className="stats-row-colon">:</span>
                    <span className="stats-row-value">{data.count}</span>
                  </div>
                  <div className="stats-row">
                    <span className="stats-row-label">Balance</span>
                    <span className="stats-row-colon">:</span>
                    <span className="stats-row-value">{formatNumber(data.totalBalance)}</span>
                  </div>
                  <div className="stats-row">
                    <span className="stats-row-label">Credit Ref</span>
                    <span className="stats-row-colon">:</span>
                    <span className="stats-row-value">{formatNumber(data.totalCredit)}</span>
                  </div>
                  <div className="stats-row">
                    <span className="stats-row-label">Exposure</span>
                    <span className="stats-row-colon">:</span>
                    <span className={`stats-row-value ${data.totalExposure < 0 ? "red" : ""}`}>
                      {formatNumber(data.totalExposure)}
                    </span>
                  </div>
                  <div className="stats-row">
                    <span className="stats-row-label">Profit/Loss</span>
                    <span className="stats-row-colon">:</span>
                    <span className={`stats-row-value ${data.totalPl >= 0 ? "green" : "red"}`}>
                      {formatNumber(data.totalPl)}
                    </span>
                  </div>
                  <div className="stats-row">
                    <span className="stats-row-label">Active</span>
                    <span className="stats-row-colon">:</span>
                    <span className="stats-row-value green">{data.active}</span>
                  </div>
                  <div className="stats-row">
                    <span className="stats-row-label">Inactive</span>
                    <span className="stats-row-colon">:</span>
                    <span className="stats-row-value red">{data.inactive}</span>
                  </div>
                </div>
                <div className="stats-card-divider" />
              </div>
            );
          })}

        {/* User Metrics Card */}
        <div className="stats-card full-width">
          <div className="stats-card-header">
            <h3>User Metrics</h3>
            <div className="subtitle">Account overview</div>
          </div>
          <div className="stats-card-body">
            <div className="metrics-row">
              <span className="metrics-row-label">Active Users</span>
              <span className="metrics-row-value">{loginUsers}</span>
            </div>
            <div className="metrics-row">
              <span className="metrics-row-label">Inactive Users</span>
              <span className="metrics-row-value">{notLoginUsers}</span>
            </div>
            <div className="metrics-row">
              <span className="metrics-row-label">Bet Locked Users</span>
              <span className="metrics-row-value">{blockedUsers}</span>
            </div>
            <div className="metrics-row">
              <span className="metrics-row-label">Masters</span>
              <span className="metrics-row-value">{masterCount}</span>
            </div>
            <div className="metrics-row">
              <span className="metrics-row-label">Agents</span>
              <span className="metrics-row-value">{agentCount}</span>
            </div>
            <div className="metrics-row">
              <span className="metrics-row-label">Clients</span>
              <span className="metrics-row-value">{userCount}</span>
            </div>
            <div className="metrics-row">
              <span className="metrics-row-label">Total Active Users</span>
              <span className="metrics-row-value">{totalCount}</span>
            </div>
          </div>
        </div>
      </div>

      {/* User Detail Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent bg="white" maxW={{ base: "95%", md: "700px" }}>
          <ModalHeader color="#222" fontSize="18px" fontWeight="700" borderBottomWidth="1px" borderColor="#eee">
            {modalTitle}
          </ModalHeader>
          <ModalCloseButton color="#222" />
          <ModalBody pb={6} px={{ base: 3, md: 6 }}>
            <div className="user-modal-controls">
              <div className="show-control">
                <span>Show:</span>
                <select
                  value={modalPageSize}
                  onChange={(e) => {
                    setModalPageSize(Number(e.target.value));
                    setModalPage(0);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <div className="search-control">
                <span>Search</span>
                <input
                  type="text"
                  placeholder="Search..."
                  value={modalSearch}
                  onChange={(e) => setModalSearch(e.target.value)}
                />
              </div>
            </div>
            <Box overflowX="auto" borderRadius="6px" border="1px solid #eee">
              <table className="user-detail-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Balance</th>
                    <th>Type</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedModalClients.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ textAlign: "center", padding: "20px", color: "#888" }}>
                        No users found
                      </td>
                    </tr>
                  ) : (
                    displayedModalClients.map((client, idx) => (
                      <tr key={client.uid || idx}>
                        <td>{client.username}</td>
                        <td>{formatNumber(client.balance)}</td>
                        <td>{typeLabels[client.userType] || client.userType}</td>
                        <td>
                          <span
                            style={{
                              color: client.isActive ? "#198754" : "#dc3545",
                              fontWeight: 600,
                            }}
                          >
                            {client.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </Box>
            {filteredModalClients.length > 0 && (
              <div className="modal-pagination">
                <span className="modal-pagination-info">
                  Showing {startIdx + 1}–{Math.min(startIdx + modalPageSize, filteredModalClients.length)} of {filteredModalClients.length} entries
                </span>
                <div className="modal-pagination-buttons">
                  <button
                    className="modal-page-btn"
                    disabled={modalPage === 0}
                    onClick={() => setModalPage((p) => p - 1)}
                  >
                    Previous
                  </button>
                  <span className="modal-page-indicator">
                    Page {modalPage + 1} of {totalModalPages}
                  </span>
                  <button
                    className="modal-page-btn"
                    disabled={modalPage >= totalModalPages - 1}
                    onClick={() => setModalPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
